/**
 * Testimonial Component for Puck Editor
 * 
 * Testimoni pelanggan - penting untuk social proof UMKM
 */

import type { ComponentConfig } from '@measured/puck'

export interface TestimonialProps {
  quote: string
  authorName: string
  authorTitle: string
  authorImage: string
  rating: 0 | 1 | 2 | 3 | 4 | 5
  variant: 'card' | 'simple' | 'featured'
}

// Star icon
const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    className={`w-5 h-5 ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
)

const RatingStars = ({ rating }: { rating: number }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <StarIcon key={star} filled={star <= rating} />
    ))}
  </div>
)

export function Testimonial({
  quote,
  authorName,
  authorTitle,
  authorImage,
  rating,
  variant,
}: TestimonialProps) {
  if (variant === 'featured') {
    return (
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          {rating > 0 && (
            <div className="flex justify-center mb-6">
              <RatingStars rating={rating} />
            </div>
          )}
          <blockquote className="text-xl md:text-2xl text-gray-800 font-medium mb-8 leading-relaxed">
            "{quote}"
          </blockquote>
          <div className="flex items-center justify-center gap-4">
            {authorImage && (
              <img
                src={authorImage}
                alt={authorName}
                className="w-16 h-16 rounded-full object-cover border-4 border-white shadow"
              />
            )}
            <div className="text-left">
              <p className="font-semibold text-gray-900">{authorName}</p>
              {authorTitle && (
                <p className="text-sm text-gray-600">{authorTitle}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (variant === 'card') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {rating > 0 && (
          <div className="mb-4">
            <RatingStars rating={rating} />
          </div>
        )}
        <blockquote className="text-gray-700 mb-6 leading-relaxed">
          "{quote}"
        </blockquote>
        <div className="flex items-center gap-3">
          {authorImage ? (
            <img
              src={authorImage}
              alt={authorName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-semibold text-lg">
                {authorName.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-900">{authorName}</p>
            {authorTitle && (
              <p className="text-sm text-gray-500">{authorTitle}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Simple variant
  return (
    <div className="border-l-4 border-primary-500 pl-6 py-2">
      <blockquote className="text-gray-700 italic mb-4">
        "{quote}"
      </blockquote>
      <div className="flex items-center gap-3">
        {rating > 0 && <RatingStars rating={rating} />}
        <span className="text-gray-400">—</span>
        <span className="font-medium text-gray-900">{authorName}</span>
        {authorTitle && (
          <span className="text-gray-500 text-sm">{authorTitle}</span>
        )}
      </div>
    </div>
  )
}

export const TestimonialConfig: ComponentConfig<TestimonialProps> = {
  label: 'Testimoni',
  defaultProps: {
    quote: 'Produknya sangat bagus dan pengiriman cepat. Terima kasih!',
    authorName: 'Budi Santoso',
    authorTitle: 'Pelanggan Setia',
    authorImage: '',
    rating: 5,
    variant: 'card',
  },
  fields: {
    quote: {
      type: 'textarea',
      label: 'Kutipan',
    },
    authorName: {
      type: 'text',
      label: 'Nama',
    },
    authorTitle: {
      type: 'text',
      label: 'Jabatan/Keterangan',
    },
    authorImage: {
      type: 'text',
      label: 'URL Foto',
    },
    rating: {
      type: 'select',
      label: 'Rating',
      options: [
        { label: 'Tanpa Rating', value: 0 },
        { label: '1 Bintang', value: 1 },
        { label: '2 Bintang', value: 2 },
        { label: '3 Bintang', value: 3 },
        { label: '4 Bintang', value: 4 },
        { label: '5 Bintang', value: 5 },
      ],
    },
    variant: {
      type: 'select',
      label: 'Gaya',
      options: [
        { label: 'Card', value: 'card' },
        { label: 'Simple', value: 'simple' },
        { label: 'Featured', value: 'featured' },
      ],
    },
  },
  render: Testimonial,
}
